import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SlackLogoPrimary from '../../../../../assets/slack-logo-primary.svg';
import SimpleDrawer, {
  type SimpleDrawerAction,
} from '../../../../../shared/simple-drawer-new';
import SharedReviewLayoutMainCard, {
  type MainSectionItem,
} from '../../../../../shared/shared-review-layout-main-card';
import SharedReviewLayoutNew, {
  type ReviewSection,
} from '../../../../../shared/shared-review-layout-new';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export interface SlackCollection {
  id: string;
  name: string;
  description: string;
  channels: { id: string; name: string }[];
  createTime: string;
  updateTime?: string;
}

interface SlackCollectionDetailsDrawerProps {
  collection: SlackCollection | null;
  onClose: () => void;
  onEdit?: (collection: SlackCollection) => void;
  onDelete?: (collection: SlackCollection) => void;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString('en-US');
}

export default function SlackCollectionDetailsDrawer({
  collection,
  onClose,
  onEdit,
  onDelete,
}: Readonly<SlackCollectionDetailsDrawerProps>) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const actionsMenu: SimpleDrawerAction[] = [
    {
      label: 'Edit Collection',
      icon: <EditOutlinedIcon fontSize="small" />,
      onClick: () => {
        if (collection) {
          onEdit?.(collection);
        }
      },
      disabled: false,
    },
    {
      label: 'Delete Collection',
      icon: <DeleteOutlineIcon fontSize="small" />,
      onClick: () => {
        if (collection) {
          onDelete?.(collection);
        }
      },
      disabled: false,
    },
  ];

  const mainSection: MainSectionItem = {
    mainTitle: collection?.name ?? '',
    fields: [
      {
        label: 'Last Updated',
        value: formatDateTime(
          collection?.updateTime ?? collection?.createTime ?? ''
        ),
      },
      {
        label: 'Created',
        value: formatDateTime(collection?.createTime ?? ''),
      },
    ],
  };

  const leftSections: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftWidth: {
        labelWidth: 5,
        valueWidth: 7,
      },
      leftItems: [
        {
          label: 'Collection Name',
          value: collection?.name ?? '',
        },
        {
          label: 'Description',
          value: collection?.description || 'No description',
        },
      ],
    },
    {
      title: 'Slack Channels',
      leftItems: [
        {
          value: (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {collection?.channels.map((channel) => (
                <Chip
                  key={channel.id}
                  label={channel.name}
                  icon={
                    <Box
                      component="img"
                      src={SlackLogoPrimary}
                      alt=""
                      sx={{
                        width: 20,
                        height: 20,
                        filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                      }}
                    />
                  }
                  variant="outlined"
                  sx={{
                    height: 46,
                    maxWidth: '100%',
                    pl: 1,
                    border: 1,
                    borderColor: theme.palette.semantic.text.title2,
                    color: theme.palette.semantic.text.title2,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    '& .MuiChip-icon': { ml: 0.5, mr: 0.45 },
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ))}
              {(!collection?.channels || collection.channels.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No Slack channels in this collection.
                </Typography>
              )}
            </Stack>
          ),
        },
      ],
    },
  ];

  return (
    <SimpleDrawer
      title="Slack Collection Details"
      open={Boolean(collection)}
      onClose={onClose}
      actionsMenu={collection ? actionsMenu : undefined}
    >
      {collection && (
        <Stack spacing={2} sx={{ width: '100%' }}>
          <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />
          <SharedReviewLayoutNew leftSections={leftSections} isDrawer={true} />
        </Stack>
      )}
    </SimpleDrawer>
  );
}
